import { LuCalendarClock, LuEqualApproximately, LuNetwork, LuPercent, LuPin, LuRocket, LuSprout } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { Account } from '@ndvr/portfolio-api';

import { AccountAssetAllocation } from '@/cip/_openapi/portfolio-optimizer';
import { useAssetClassExposureByAccount } from '@/cip/api/assetAllocation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    Table as BaseTable,
    TableBody as BaseTableBody,
    TableCell as BaseTableCell,
    TableHead as BaseTableHead,
    TableHeader as BaseTableHeader,
    TableRow as BaseTableRow,
} from '@/components/ui/table';
import {
    formatAccountType,
    formatCurrency,
    formatDate,
    formatPercent,
    useHouseholdRoutePaths,
    useRequiredParams,
} from 'utils';
import { ALLOCATION_SEGMENT_COLORS } from 'views/household/asset-allocation-presentation';
import { useListCashflowConfigs } from 'views/portfolio/account-group-queries';
import { getNextPolicyWithdrawal } from 'views/portfolio/policy-withdrawals';

import { getCurrentAllocation } from '../overview/current-allocation';
import { summarizePolicyExclusions } from './exclusions/policy-exclusions';
import { usePolicyExclusions } from './exclusions/usePolicyExclusions';
import {
    describePolicyTaxBudget,
    PolicySummaryValue,
    summarizePolicyTaxBudget,
    summarizePolicyTaxLossHarvesting,
    summarizePolicyToggle,
} from './investment-policy-overview';
import { PolicyAllocation } from './PolicyAllocation';
import { PolicyQueryError } from './PolicyQueryError';
import {
    PolicyTable as Table,
    PolicyTableBody as TableBody,
    PolicyTableCell as TableCell,
    PolicyTableHead as TableHead,
    PolicyTableHeader as TableHeader,
    PolicyTableRow as TableRow,
} from './PolicyTable';
import { usePolicyScope } from './usePolicyScope';

const PolicyAccountRow = ({
    account,
    householdGUID,
    currentAccountGUID,
    exposures,
    isLoading,
    isError,
    portfolioTotal,
}: {
    account: Account;
    householdGUID: string;
    currentAccountGUID: string;
    exposures?: AccountAssetAllocation[];
    isLoading: boolean;
    isError: boolean;
    portfolioTotal?: number;
}) => {
    const { AccountPageRoutePath } = useHouseholdRoutePaths();
    const allocation = exposures ? getCurrentAllocation(exposures) : undefined;
    const unavailable = isLoading ? 'Loading…' : '—';
    return (
        <TableRow>
            <TableCell className="min-w-48 py-4">
                <Link
                    to={`${AccountPageRoutePath(householdGUID, account.accountGroupGUID, account.accountGUID)}/investment-policy`}
                    className="text-cocoa-900 font-medium hover:underline"
                >
                    {account.accountNamePlain ?? account.accountName.plainText}
                </Link>
                <div className="text-cocoa-500 mt-1 flex flex-wrap items-center gap-2 text-xs">
                    {formatAccountType(account.accountType)}
                    {account.accountGUID === currentAccountGUID && (
                        <span className="bg-cocoa-0 rounded px-1.5 py-0.5">This account</span>
                    )}
                </div>
            </TableCell>
            {(['equities', 'bonds', 'cash'] as const).map((key) => {
                const amount = allocation?.segments.find((segment) => segment.key === key)?.amount ?? 0;
                return (
                    <TableCell key={key} className="text-right tabular-nums">
                        {allocation ? (
                            <>
                                <div>
                                    {formatPercent(allocation.total > 0 ? (amount / allocation.total) * 100 : 0, 1)}
                                </div>
                                <div className="text-cocoa-500 mt-1 text-xs">{formatCurrency(amount)}</div>
                            </>
                        ) : (
                            unavailable
                        )}
                    </TableCell>
                );
            })}
            <TableCell className="min-w-36">
                {allocation ? <PolicyAllocation allocation={allocation} /> : <span>{unavailable}</span>}
            </TableCell>
            <TableCell className="min-w-36 text-right tabular-nums">
                <div className="font-medium">{allocation ? formatCurrency(allocation.total) : unavailable}</div>
                {allocation && portfolioTotal !== undefined && portfolioTotal > 0 && (
                    <div className="text-cocoa-500 mt-1 text-xs">
                        {formatPercent((allocation.total / portfolioTotal) * 100, 1)} of portfolio
                    </div>
                )}
                {isError && <span className="text-cocoa-500 text-xs">Unable to load allocation</span>}
            </TableCell>
        </TableRow>
    );
};

export const InvestmentPolicyOverview = ({ onTaxBudgetSelect }: { onTaxBudgetSelect: () => void }) => {
    const { globalAccountGUID, accountGroupGUID, accountGUID } = useRequiredParams([
        'globalAccountGUID',
        'accountGroupGUID',
        'accountGUID',
    ]);
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const scope = usePolicyScope(accountGUID, accountGroupGUID);
    const { account, accounts: policyAccounts } = scope;
    const profile = scope.status === 'ready' ? scope.profile : undefined;
    const scopeAvailable = scope.status === 'ready';
    const isScopePending = scope.status === 'loading';
    const exclusions = usePolicyExclusions(policyAccounts);
    const coordinated = policyAccounts.length > 1;
    const scopeStatus = isScopePending ? 'Loading…' : 'Unavailable';
    const scopeMessage = isScopePending ? 'Loading policy scope…' : 'Policy scope unavailable';
    const policyBrokerAccountIds = policyAccounts.flatMap((item) => item.brokerAccountId ?? []);
    const exposuresQuery = useAssetClassExposureByAccount(policyBrokerAccountIds, { throwOnError: false });
    const { data: exposuresByAccount, isLoading: isAllocationLoading } = exposuresQuery;
    const isAllocationError = exposuresQuery.isLoadingError;
    const cashflowsQuery = useListCashflowConfigs(accountGroupGUID, undefined, { throwOnError: false });
    const { data: cashflows, isLoading: isWithdrawalLoading } = cashflowsQuery;
    const isCashflowError = cashflowsQuery.isLoadingError;
    const retryQueries = [...scope.retryQueries, exposuresQuery, cashflowsQuery, ...exclusions.retryQueries];
    const hasFailure = scope.status === 'error' || isAllocationError || isCashflowError || exclusions.isError;
    if (!account) {
        if (scope.status === 'error')
            return <PolicyQueryError message="Couldn’t load the investment policy." queries={scope.retryQueries} />;
        return <p className="text-cocoa-600 py-8">Loading account…</p>;
    }

    const allocation =
        scopeAvailable && exposuresByAccount
            ? getCurrentAllocation(Object.values(exposuresByAccount).flat())
            : undefined;
    const nextWithdrawal =
        profile && cashflows ? getNextPolicyWithdrawal(cashflows, policyAccounts, profile.accountGroup) : undefined;
    const withdrawal: PolicySummaryValue = isCashflowError
        ? { text: 'Unable to load withdrawals', active: false }
        : isWithdrawalLoading
          ? { text: 'Loading…', active: false }
          : nextWithdrawal
            ? {
                  text: `${formatDate(nextWithdrawal.date, 'MMM d')} · ${formatCurrency(nextWithdrawal.amount)}`,
                  active: true,
              }
            : { text: 'None scheduled', active: false };
    const taxBudget = summarizePolicyTaxBudget(policyAccounts);
    const exclusionCount = exclusions.rows.length;
    const { total: exclusionTotal } = summarizePolicyExclusions(
        exclusions.rows,
        policyAccounts,
        profile?.accounts ?? [],
        accountGUID,
    );
    const exclusionSummary: PolicySummaryValue = exclusions.isError
        ? { text: 'Unable to load exclusions', active: false }
        : exclusions.isPending
          ? { text: 'Loading…', active: false }
          : exclusionCount
            ? {
                  text: `${formatCurrency(exclusionTotal)} · ${exclusionCount} holding${exclusionCount === 1 ? '' : 's'}`,
                  active: true,
              }
            : { text: 'None', active: false };
    const summary = scopeAvailable
        ? [
              {
                  label: 'Coordinated',
                  value: coordinated
                      ? { text: `${policyAccounts.length} accounts`, active: true }
                      : { text: 'Off', active: false },
                  icon: LuNetwork,
                  color: 'bg-gold-300 text-white',
              },
              {
                  label: 'Cash EQ',
                  value: summarizePolicyToggle(policyAccounts.map((item) => item.cashEqSweep)),
                  icon: LuSprout,
                  color: 'bg-positive/10 text-positive',
              },
              {
                  label: 'Tax budget',
                  value: describePolicyTaxBudget(taxBudget, coordinated),
                  icon: LuPercent,
                  color: 'bg-positive/10 text-positive',
                  action: { label: 'View tax budgets', onSelect: onTaxBudgetSelect },
                  warning:
                      taxBudget.kind === 'unlimited' && taxBudget.inconsistent
                          ? {
                                text: 'Inconsistent account budgets',
                                title: 'Taxable accounts mix unlimited and limited tax budgets.',
                            }
                          : undefined,
              },
              {
                  label: 'Tax-loss harvesting',
                  value: summarizePolicyTaxLossHarvesting(policyAccounts),
                  icon: LuEqualApproximately,
                  color: 'bg-blue-50 text-blue-700',
              },
              {
                  label: 'Next Planned Withdrawal',
                  value: withdrawal,
                  icon: LuCalendarClock,
                  color: 'bg-cyan-50 text-cyan-600',
              },
              {
                  label: 'Exclusions',
                  value: exclusionSummary,
                  icon: LuPin,
                  color: 'bg-red-0 text-red-300',
              },
          ]
        : [];
    const portfolioPath = PortfolioPageRoutePath(globalAccountGUID, accountGroupGUID);

    return (
        <div className="space-y-4.5">
            {hasFailure && (
                <PolicyQueryError message="Some overview information could not be loaded." queries={retryQueries} />
            )}
            {!scopeAvailable && (
                <p className="text-cocoa-600 text-sm" role="status">
                    {isScopePending
                        ? 'Loading policy scope…'
                        : 'Policy scope is unavailable. Allocation totals, account membership, and Boost settings cannot be shown.'}
                </p>
            )}
            {coordinated && (
                <div className="border-cocoa-100 flex flex-wrap items-center gap-3 rounded-lg border border-l-[3px] border-l-teal-700 px-4.5 py-3.5 text-sm">
                    <p className="text-cocoa-700 min-w-0 flex-1">
                        Changes apply to all {policyAccounts.length} accounts in{' '}
                        {profile?.accountGroup.accountGroupNamePlain ??
                            profile?.accountGroup.accountGroupName.plainText}
                    </p>
                    <Button asChild variant="ghost" size="sm">
                        <Link to={`${portfolioPath}/overview`}>Manage accounts</Link>
                    </Button>
                </div>
            )}
            <div className="grid gap-4.5 xl:grid-cols-2">
                <Card className="border-cocoa-100 rounded-xl p-6">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h2 className="text-cocoa-600 text-xs font-medium tracking-wider uppercase">
                                {!scopeAvailable ? 'Policy value' : coordinated ? 'Portfolio value' : 'Account value'}
                            </h2>
                            <div className="text-cocoa-900 mt-1.5 text-[34px] font-medium tabular-nums">
                                {!scopeAvailable
                                    ? scopeStatus
                                    : allocation
                                      ? formatCurrency(allocation.total)
                                      : isAllocationLoading
                                        ? 'Loading…'
                                        : '—'}
                            </div>
                        </div>
                        <PolicyAllocation allocation={allocation ?? { total: 0, segments: [] }} donut />
                    </div>
                    <BaseTable className="text-cocoa-1000 [&_th]:text-cocoa-600 mt-5 border-b border-stone-100 [&_td]:px-0 [&_th]:h-auto [&_th]:px-0 [&_th]:py-2 [&_th]:text-[11px] [&_th]:font-medium [&_th]:tracking-[0.08em] [&_th]:uppercase [&_tr]:border-stone-100 [&_tr:hover]:bg-transparent">
                        <BaseTableHeader>
                            <BaseTableRow>
                                <BaseTableHead>Asset class</BaseTableHead>
                                <BaseTableHead className="text-right">Weight</BaseTableHead>
                                <BaseTableHead className="text-right">Value</BaseTableHead>
                            </BaseTableRow>
                        </BaseTableHeader>
                        <BaseTableBody>
                            {allocation?.segments.map((segment) => (
                                <BaseTableRow key={segment.key}>
                                    <BaseTableCell className="py-3 font-medium">
                                        <span className="flex items-center gap-2.5">
                                            <span
                                                className={`h-2 w-2 shrink-0 rounded-sm ${ALLOCATION_SEGMENT_COLORS[segment.key]}`}
                                            />
                                            {segment.key === 'cash' ? 'Cash' : segment.label}
                                        </span>
                                    </BaseTableCell>
                                    <BaseTableCell className="py-3 text-right font-medium tabular-nums">
                                        {formatPercent((segment.amount / allocation.total) * 100, 1)}
                                    </BaseTableCell>
                                    <BaseTableCell className="text-cocoa-800 py-3 text-right tabular-nums">
                                        {formatCurrency(segment.amount)}
                                    </BaseTableCell>
                                </BaseTableRow>
                            ))}
                            {!allocation?.segments.length && (
                                <BaseTableRow>
                                    <BaseTableCell colSpan={3} className="text-cocoa-500 py-6 text-center">
                                        {!scopeAvailable
                                            ? scopeMessage
                                            : isAllocationError
                                              ? 'Unable to load allocation'
                                              : isAllocationLoading
                                                ? 'Loading allocation…'
                                                : 'No holdings available'}
                                    </BaseTableCell>
                                </BaseTableRow>
                            )}
                        </BaseTableBody>
                    </BaseTable>
                </Card>
                <Card className="border-cocoa-100 rounded-xl p-6">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-cocoa-600 text-xs font-medium tracking-wider uppercase">Boost summary</h2>
                        <span className="bg-cocoa-800 flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium tracking-wider text-white uppercase">
                            <LuRocket aria-hidden="true" className="h-3.5 w-3.5" /> Boost enabled
                        </span>
                    </div>
                    {!scopeAvailable && (
                        <p className="text-cocoa-500 border-cocoa-100 border-t py-6 text-center text-sm">
                            {scopeMessage}
                        </p>
                    )}
                    <dl className="grid gap-x-6 sm:grid-cols-2">
                        {summary.map(({ label, value, icon: Icon, color, action, warning }) => (
                            <div
                                key={label}
                                className="border-cocoa-100 flex min-w-0 items-center gap-3 border-t py-3.5"
                            >
                                <div
                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${value.active ? color : 'bg-cocoa-0 text-cocoa-400'}`}
                                >
                                    <Icon aria-hidden="true" className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <dt className="text-cocoa-600 text-[13px]">{label}</dt>
                                    <dd
                                        className={`text-sm font-medium ${value.active ? 'text-cocoa-900' : 'text-cocoa-600'}`}
                                    >
                                        {action ? (
                                            <button
                                                type="button"
                                                onClick={action.onSelect}
                                                aria-label={`${action.label}: ${value.text}`}
                                                className="focus-visible:ring-cocoa-500 cursor-pointer rounded text-left hover:underline focus-visible:ring-2 focus-visible:outline-none"
                                            >
                                                {value.text}
                                            </button>
                                        ) : (
                                            value.text
                                        )}
                                        {warning && (
                                            <p
                                                className="mt-1 text-xs font-normal text-amber-700"
                                                title={warning.title}
                                            >
                                                {warning.text}
                                            </p>
                                        )}
                                    </dd>
                                </div>
                            </div>
                        ))}
                    </dl>
                </Card>
            </div>
            {coordinated && (
                <Table>
                    <TableHeader>
                        <TableRow>
                            {['Account', 'Equities', 'Bonds', 'Cash', 'Allocation', 'Value'].map((heading) => (
                                <TableHead
                                    key={heading}
                                    className={
                                        heading === 'Account' || heading === 'Allocation' ? undefined : 'text-right'
                                    }
                                >
                                    {heading}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {policyAccounts.map((item) => (
                            <PolicyAccountRow
                                key={item.accountGUID}
                                account={item}
                                householdGUID={globalAccountGUID}
                                currentAccountGUID={accountGUID}
                                exposures={
                                    item.brokerAccountId ? exposuresByAccount?.[item.brokerAccountId] : undefined
                                }
                                isLoading={!!item.brokerAccountId && isAllocationLoading}
                                isError={!!item.brokerAccountId && isAllocationError}
                                portfolioTotal={allocation?.total}
                            />
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};
